import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FeeData } from '../types';
import valoriLogo from '../../../assets/logo.png';


const formatFee = (fee?: number) => {
  if (!fee || fee === 0) return '-';
  return `${fee.toFixed(2).replace('.', ',')}%`;
};

const formatDateTime = () => {
  const now = new Date();
  return `${now.toLocaleDateString('pt-BR')} às ${now.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  })}`;
};

const loadImage = (src: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext('2d')?.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = src;
  });

/* PDF */
export const generateFeesPdf = async (
  data: FeeData[],
  anticipationFee: number,
  digitalAnticipationFee: number,
  anticipationType: 'DIGITAL' | 'POS'
) => {
  const doc = new jsPDF('landscape');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  /* Razão social*/
  const storedCompany = JSON.parse(
    localStorage.getItem('selectedCompany') || '{}'
  );

  const companyLabel = storedCompany?.name ?? '';

  /*Imagens*/
  const [
    valori,
    visa,
    mastercard,
    elo,
    amex,
    hipercard
  ] = await Promise.all([
    loadImage(valoriLogo),
    loadImage('https://raw.githubusercontent.com/aaronfagan/svg-credit-card-payment-icons/main/flat-rounded/visa.svg'),
    loadImage('https://raw.githubusercontent.com/aaronfagan/svg-credit-card-payment-icons/main/flat-rounded/mastercard.svg'),
    loadImage('https://raw.githubusercontent.com/arthurmourab/svg-credit-card-payment-icons/refs/heads/main/flat-rounded/elo.svg'),
    loadImage('https://raw.githubusercontent.com/aaronfagan/svg-credit-card-payment-icons/main/flat-rounded/amex.svg'),
    loadImage('https://raw.githubusercontent.com/aaronfagan/svg-credit-card-payment-icons/main/flat-rounded/hipercard.svg')
  ]);

  const cardLogos: Record<number, string> = {
    1: visa,
    2: mastercard,
    3: elo,
    4: amex,
    5: hipercard
  };

  /*Título*/
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(0);
  doc.text('TABELA DE TAXAS', pageWidth / 2, 18, { align: 'center' });

  if (companyLabel) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(companyLabel, pageWidth / 2, 24, { align: 'center' });
  }

  /*Tabela principal*/
  autoTable(doc, {
    startY: 32,
    showHead: 'firstPage',
    head: [[
      'TIPO (MODALIDADE)',
      '',
      '',
      '',
      '',
      '',
      'PRAZO DE RECEBIMENTO',
      'CAPTURA'
    ]],
    body: data.map((item, index) => {
      const posFee = anticipationFee ?? 0;
      const digitalFee = digitalAnticipationFee ?? 0;

      let settlement = `D+${index === 0 ? 1 : index * 30}`;

      if (item.captureChannel === 1) {
        if (digitalFee > 0) settlement = 'D+1';
      }

      if (item.captureChannel === 0) {
        if (posFee > 0) settlement = 'D+1';
      }

      return [
        item.modality,
        formatFee(item.fees.visa),
        formatFee(item.fees.mastercard),
        formatFee(item.fees.elo),
        formatFee(item.fees.amex),
        formatFee(item.fees.hipercard),
        settlement,
        item.captureChannel === 0 ? 'POS' : 'E-commerce'
      ];
    }),
    styles: {
      fontSize: 10,
      halign: 'center',
      valign: 'middle',
      lineWidth: 0,
      fillColor: [255, 255, 255]
    },
    headStyles: {
      fillColor: [225, 225, 225],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      minCellHeight: 24
    },
    bodyStyles: {
      fillColor: [255, 255, 255]
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255]
    },
    columnStyles: {
      0: {
        halign: 'left',
        fontStyle: 'bold'
      }
    },
    didDrawCell: (hook) => {
      if (hook.section === 'head') {
        const img = cardLogos[hook.column.index];
        if (img) {
          const logoWidth = 18;
          const logoHeight = 12;

          const x = hook.cell.x + (hook.cell.width - logoWidth) / 2;
          const y = hook.cell.y + (hook.cell.height - logoHeight) / 2;
          doc.addImage(img, 'PNG', x, y, logoWidth, logoHeight);
        }
      }
    }
  });

  /*Taxa de antecipação */
  const finalY = (doc as any).lastAutoTable.finalY + 8;

  autoTable(doc, {
    startY: finalY,
    body: [[
      anticipationType === 'DIGITAL'
        ? 'TAXA DE ANTECIPAÇÃO DIGITAL'
        : 'TAXA DE ANTECIPAÇÃO',
      formatFee(anticipationType === 'DIGITAL'
        ? (digitalAnticipationFee ?? 0)
        : (anticipationFee ?? 0))
    ]],
    styles: {
      fontSize: 11,
      fontStyle: 'bold',
      halign: 'center',
      fillColor: [255, 255, 255]
    },
    columnStyles: {
      0: { cellWidth: 120 },
      1: { cellWidth: 40 }
    },
    margin: { left: (pageWidth - 160) / 2 }
  });

  /* Informações finais (última página)*/
  const lastPage = doc.getNumberOfPages();
  doc.setPage(lastPage);

  const infoY = pageHeight - 25;
  const logoX = pageWidth - 100;

  doc.addImage(valori, 'PNG', logoX, infoY - 2, 20, 22);

  const textX = logoX + 26;
  doc.setFontSize(9);
  doc.setTextColor(0);

  doc.text('Atendimento 4000-1708', textX, infoY);
  doc.text('valori.com.vc | contato@valori.com.vc', textX, infoY + 5);
  doc.text('R. 72, 325 - Sala 805', textX, infoY + 10);
  doc.text('Edifício Trend Office - Jardim Goiás', textX, infoY + 15);
  doc.text('Goiânia - GO | CEP: 74805-480', textX, infoY + 20);

  /* Rodapé */
  for (let i = 1; i <= lastPage; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(
      `Relatório gerado em ${formatDateTime()}`,
      14,
      pageHeight - 8
    );
  }

  doc.save('tabela-de-taxas.pdf');
};
